import axios, { AxiosInstance, AxiosHeaders } from 'axios';
import configuration, { loginRequest, isMsal } from '../config'
import { msalReady } from '../auth/msal';

// Wire up interceptors
const instance: AxiosInstance = axios.create();

// intercepts unauthorized access
instance.interceptors.response.use(
    function (response) {
        return response;
    },
    async function (error) {
        if (401 === error.response.status) {
            const msal = await msalReady!;
            const account = msal.getAllAccounts()[0];

            const tokenResponse = await msal.acquireTokenSilent({ ...loginRequest, account });
            error.config.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;

            return instance(error.config);
        } else {
            return Promise.reject(error);
        }
    }
);

// adds token from local storage to request headers
instance.interceptors.request.use(
    async (config) => {
        if (isMsal) {
            const msal = await msalReady!;
            const account = msal.getAllAccounts()[0];
            const response = await msal.acquireTokenSilent({ ...loginRequest, account });

            if (!config.headers) {
                config.headers = new AxiosHeaders();
            }
            config.headers['Authorization'] = `Bearer ${response.accessToken}`;

            // required for hangfire
            localStorage.setItem("token", response.accessToken);
        } else {
            const token = localStorage.getItem('token');
            if (token) {
                if (!config.headers) {
                    config.headers = new AxiosHeaders();
                }

                config.headers.Authorization = `Bearer ${token}`;
            }
        }

        return config;
    },
    error => {
        Promise.reject(error)
    });

export default class BaseService {
    apiUrl: string;
    constructor(endpoint: string, serverUrl: string | undefined = configuration.API_SERVER) {
        this.apiUrl = `${serverUrl}/${endpoint}`;
    }

    httpGetRequest = (apiMethod: string | number, headers: Record<string, string> = {}) =>
        instance.get([this.apiUrl, apiMethod].join('/'), { headers });

    //POST request wrapper. pass an object/array to it. (optional uri parameters)
    httpPostRequest = <T = unknown>(apiMethod: string | number, requestBody?: T, headers: Record<string, string> = {}) =>
        instance.post([this.apiUrl, apiMethod].join('/'), requestBody, { headers, maxContentLength: Infinity });

    // PUT request wrapper
    httpPutRequest = <T = unknown>(apiMethod: string | number, requestBody?: T, headers: Record<string, string> = {}) =>
        instance.put([this.apiUrl, apiMethod].join('/'), requestBody, { headers });

    // DELETE request wrapper
    httpDeleteRequest = <T = unknown>(apiMethod: string | number, requestBody?: T, headers: Record<string, string> = {}) =>
        instance.delete([this.apiUrl, apiMethod].join('/'), { data: requestBody, headers });

    // PATCH request wrapper
    httpPatchRequest = <T = unknown>(apiMethod: string | number, requestBody?: T, headers: Record<string, string> = {}) =>
        instance.patch([this.apiUrl, apiMethod].join('/'), requestBody, { headers });

    generateQueryString(parameters: Record<string, any>): string {
        const params = new URLSearchParams();

        for (const [paramName, paramValue] of Object.entries(parameters)) {
            if (Array.isArray(paramValue)) {
                for (const arrayValue of paramValue) {
                    params.append(paramName, arrayValue.toString());
                }
            } else if (paramValue !== undefined) {
                params.append(paramName, paramValue.toString())
            }
        }

        return params.toString();
    }
}
